/**
 * PlanningTabs — Planning is één scherm met weergaven (ronde 4, besluit Joris
 * 25 sep 2026): Rooster · Capaciteit · Nog in te plannen · Dagstart, sinds 26 sep ook
 * Draaidagen · Materialen (gebruiksanalyse: de langst openstaande schermen). De tabbalk
 * staat in de kopbalk van de AppShell (op de plek van het kruimelpad), zodat hij op elke
 * weergave op exact dezelfde plek staat en de pagina's zelf geen extra kopregel krijgen.
 * Tabs zonder featurerecht verdwijnen. Rechts een stille link naar de hub met de
 * overige planningstools (Weekbord, Corvee, Voertuigen, …).
 */
import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useUserRole } from '../contexts/UserRoleContext';
import { getPlanningTabs, SECONDARY_NAV_SECTIONS } from '../config/navigation';
import { useMsal } from '@azure/msal-react';
import { laadAgendaVooruit, wekenVoorPad } from '../utils/agendaVooruit';

const PAARS = '#635983';
const FONT_BODY = '"Inter", system-ui, sans-serif';

export default function PlanningTabs() {
  const { pathname } = useLocation();
  const { permissions } = useUserRole();
  const tabs = getPlanningTabs(permissions);
  const hub = SECONDARY_NAV_SECTIONS.planning;
  // Planning geopend: de agenda alvast in de gedeelde cache, in het venster dat dít scherm
  // zelf vraagt (Capaciteit 1 week, Rooster/planbord 2 — zie wekenVoorPad), zodat het
  // hem direct heeft. Hooguit één keer per minuut per account en venster, en een lopende
  // of verse aanvraag wordt hergebruikt (geen extra aanvragen bij tabwissels).
  const { accounts } = useMsal();
  const account = accounts?.[0]?.username || '';
  useEffect(() => { laadAgendaVooruit({ weken: wekenVoorPad(pathname), account }); }, [pathname, account]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', alignSelf: 'stretch', flex: 1, minWidth: 0, gap: 1 }}>
      <Box
        component="nav"
        aria-label="Planning-weergaven"
        sx={{
          display: 'flex', alignItems: 'stretch', gap: { xs: 1.75, md: 2.5 },
          minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {tabs.map((t) => {
          const actief = pathname === t.path;
          return (
            <Box
              key={t.path}
              component={RouterLink}
              to={t.path}
              aria-current={actief ? 'page' : undefined}
              sx={{
                display: 'flex', alignItems: 'center', flexShrink: 0,
                fontFamily: FONT_BODY, fontSize: '0.82rem', letterSpacing: '-0.01em',
                fontWeight: actief ? 600 : 500,
                color: actief ? '#2d3335' : '#6b7280',
                textDecoration: 'none', whiteSpace: 'nowrap',
                // Onderstreping op de onderrand van de kopbalk — één rustig accent
                borderBottom: `2px solid ${actief ? PAARS : 'transparent'}`,
                borderTop: '2px solid transparent',
                '&:hover': { color: actief ? '#2d3335' : PAARS },
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>
      <Box
        component={RouterLink}
        to={hub.hubPath}
        title="Weekbord, corvee, voertuigen en de andere planningstools"
        aria-label={hub.alleLabel}
        sx={{
          ml: 'auto', mr: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
          fontFamily: FONT_BODY, fontSize: '0.76rem', color: '#9aa1a6',
          textDecoration: 'none', whiteSpace: 'nowrap',
          '&:hover': { color: PAARS },
        }}
      >
        <AppsIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{hub.alleLabel}</Box>
      </Box>
    </Box>
  );
}
